import React, {useEffect} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Button,
    Container,
    Radio,
    RadioGroup,
    Stack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm, Controller} from 'react-hook-form'
import {useCheckout} from '../../checkout/util/checkout-context'
import {
    useShippingMethodsForShipment,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {ToggleCard, ToggleCardEdit, ToggleCardSummary} from '../../../components/toggle-card'
import {pxToRem} from '../../../utills/pxToRem'
import ShippingMethodAvailableOptions from './shipping-method-available-options'
import {dispalyShipingAddress} from '../index.style'

export default function ShippingOptions() {
    const {formatMessage} = useIntl()
    const {step, STEPS, goToStep, goToNextStep, nonStorePickShipments, setCheckoutError} =
        useCheckout()
    const {data: basket} = useCurrentBasket()
    const updateShippingMethod = useShopperBasketsMutation('updateShippingMethodForShipment')
    const {data: shippingMethods} = useShippingMethodsForShipment(
        {
            parameters: {
                basketId: basket?.basketId,
                shipmentId: nonStorePickShipments[0]?.shipmentId || 'me'
            }
        },
        {
            enabled: Boolean(basket?.basketId) && step === STEPS.SHIPPING_OPTIONS
        }
    )
    const selectedShippingMethod = nonStorePickShipments[0]?.shippingMethod
    const selectedShippingAddress = nonStorePickShipments[0]?.shippingAddress
    const form = useForm({
        shouldUnregister: false,
        defaultValues: {
            shippingMethodId: selectedShippingMethod?.id || shippingMethods?.defaultShippingMethodId
        }
    })
    useEffect(() => {
        const defaultMethodId = shippingMethods?.defaultShippingMethodId
        const methodId = form.getValues().shippingMethodId
        if (!selectedShippingMethod && !methodId && defaultMethodId) {
            form.reset({shippingMethodId: defaultMethodId})
        }
        if (selectedShippingMethod && methodId !== selectedShippingMethod.id) {
            form.reset({shippingMethodId: selectedShippingMethod.id})
        }
    }, [selectedShippingMethod, shippingMethods])
    const submitForm = async ({shippingMethodId}) => {
        try {
            setCheckoutError(null)
            await updateShippingMethod.mutateAsync({
                parameters: {
                    basketId: basket.basketId,
                    shipmentId: nonStorePickShipments[0]?.shipmentId || 'me'
                },
                body: {id: shippingMethodId}
            })
            goToNextStep()
        } catch (error) {
            let errorBody
            if (error?.response instanceof Response) {
                try {
                    errorBody = await error.response.clone().json()
                } catch {
                    const text = await error.response.text()
                    try {
                        errorBody = JSON.parse(text)
                    } catch {
                        errorBody = {message: text}
                    }
                }
            } else {
                errorBody = error?.response?.data
            }

            let message = formatMessage({
                id: 'shipping_options.message.generic_error',
                defaultMessage:
                    'Oops! Somethings not right with your shipping address. Please make sure all required fields are filled in correctly.'
            })

            if (errorBody?.statusCode === 'BUS-1006' || errorBody?.statusCode === 'BUS-1005') {
                message = errorBody?.statusMessage
            }

            setCheckoutError(message)
        }
    }
    const shippingItem =
        basket?.shippingItems?.find((item) => item?.basePrice > 0) ?? basket?.shippingItems?.[0]

    const selectedMethodDisplayPrice =
        shippingItem?.priceAfterItemDiscount ?? shippingItem?.price ?? 0

    const freeLabel = formatMessage({
        defaultMessage: 'Free',
        id: 'checkout_confirmation.label.free'
    })

    let shippingPriceLabel = selectedMethodDisplayPrice
    if (
        shippingItem?.priceAfterItemDiscount != null &&
        shippingItem?.priceAfterItemDiscount < shippingItem?.price
    ) {
        const currentPrice =
            selectedMethodDisplayPrice === 0 ? freeLabel : selectedMethodDisplayPrice

        shippingPriceLabel = formatMessage(
            {
                defaultMessage: 'Originally {originalPrice}, now {newPrice}',
                id: 'checkout_confirmation.label.shipping.strikethrough.price'
            },
            {
                originalPrice: shippingItem.price,
                newPrice: currentPrice
            }
        )
    }

    return (
        <ToggleCard
            id="step-2"
            title={formatMessage({
                defaultMessage: 'Shipping Method',
                id: 'shipping_options.title.shipping_method'
            })}
            step={3}
            editing={step === STEPS.SHIPPING_OPTIONS}
            isLoading={form.formState.isSubmitting}
            disabled={selectedShippingMethod == null || !selectedShippingAddress}
            onEdit={() => goToStep(STEPS.SHIPPING_OPTIONS)}
            editLabel={formatMessage({defaultMessage: 'Edit', id: 'toggle_card.action.edit'})}
        >
            <ToggleCardSummary>
                <Box
                    display={['none', 'none', 'none', 'block']}
                    fontSize={{base: pxToRem(14), lg: pxToRem(16)}}
                    fontWeight={500}
                    lineHeight={'normal'}
                    letterSpacing={pxToRem(1)}
                    marginBottom={pxToRem(10)}
                    color="black"
                >
                    {formatMessage({
                        defaultMessage: 'Shipping Method',
                        id: 'shipping_address_selection.text.shipping_method'
                    })}
                </Box>
            </ToggleCardSummary>
            <ToggleCardEdit>
                <Box
                    fontSize={{base: pxToRem(14), lg: pxToRem(16)}}
                    lineHeight={'normal'}
                    letterSpacing={pxToRem(1)}
                    marginBottom={{base: pxToRem(15), lg: pxToRem(27)}}
                    fontWeight={500}
                    color="black"
                >
                    {formatMessage({
                        defaultMessage: 'Select Shipping Method',
                        id: 'toggle_card.action.select_shippiing_method'
                    })}
                </Box>
                <form
                    onSubmit={form.handleSubmit(submitForm)}
                    data-testid="sf-checkout-shipping-options-form"
                >
                    <Stack spacing={6}>
                        {shippingMethods?.applicableShippingMethods && (
                            <Controller
                                name="shippingMethodId"
                                control={form.control}
                                defaultValue=""
                                render={({field: {value, onChange}}) => (
                                    <RadioGroup
                                        name="shipping-options-radiogroup"
                                        value={value}
                                        onChange={onChange}
                                    >
                                        <Stack spacing={5}>
                                            <Box
                                                display={'flex'}
                                                flexWrap="wrap"
                                                gap={pxToRem(16)}
                                                sx={dispalyShipingAddress}
                                            >
                                                {shippingMethods.applicableShippingMethods.map(
                                                    (opt) => (
                                                        <Box
                                                            key={opt.id}
                                                            width={{base: '100%', lg: '48%'}}
                                                            className="radio-styles"
                                                        >
                                                            <Radio value={opt.id}>
                                                                {shippingItem.basePrice ===
                                                                opt.price ? (
                                                                    <ShippingMethodAvailableOptions
                                                                        avlOptions={opt}
                                                                        selectedMethodDisplayPrice={
                                                                            selectedMethodDisplayPrice
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <ShippingMethodAvailableOptions
                                                                        avlOptions={opt}
                                                                    />
                                                                )}
                                                            </Radio>
                                                        </Box>
                                                    )
                                                )}
                                            </Box>
                                        </Stack>
                                    </RadioGroup>
                                )}
                            />
                        )}
                        <Box>
                            <Container variant="form" margin={0} width={'100%'} maxWidth={'100%'}>
                                <Button width={'100%'} type="submit" className="button-primary">
                                    <FormattedMessage
                                        defaultMessage="Save And Continue"
                                        id="shipping_options.button.save_and_continue"
                                    />
                                </Button>
                            </Container>
                        </Box>
                    </Stack>
                </form>
            </ToggleCardEdit>
            {selectedShippingMethod && selectedShippingAddress && (
                <ToggleCardSummary>
                    <ShippingMethodAvailableOptions
                        avlOptions={selectedShippingMethod}
                        selectedMethodDisplayPrice={selectedMethodDisplayPrice}
                        shippingItemPrice={shippingItem.price}
                    />
                </ToggleCardSummary>
            )}
        </ToggleCard>
    )
}
